import React, { useMemo, useState } from 'react'
import Typography from 'antd-v4/lib/typography'
import { DatePicker, Spin } from 'antd'
import { Button, message, Modal, Select } from 'antd-v4'
import { dayjs } from '@/app/utilities/dayjs'
import { parseErrorMsg } from '@/app/utilities/helpers'
import Endpoints from '@/app/constants/Endpoints'
import {
  IResubmitComplianceContract,
  RESUBMIT_OCS_KEYS,
  resubmitCompliance,
} from '@getgreenline/ocs'

interface Props {
  crsaHashKeyMap: Map<string, string>
  onClose: () => void
}

export const OcsResubmissionModal = ({ crsaHashKeyMap, onClose }: Props) => {
  const [selectedCRSA, setSelectedCRSA] = useState<string>()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const invalidDateRange = useMemo(() => {
    if (!startDate || !endDate) return false

    const diffInMonths = dayjs(endDate).diff(dayjs(startDate), 'month', true)
    return diffInMonths > 1
  }, [startDate, endDate])

  const disableSubmit = !selectedCRSA || invalidDateRange || !startDate || !endDate || isLoading

  const reset = () => {
    setSelectedCRSA(undefined)
    setStartDate(null)
    setEndDate(null)
  }

  const createPayload = ({
    hashKey,
    type,
    startDate,
    endDate,
  }: {
    hashKey: string
    type: 'event' | 'position'
    startDate: Date
    endDate: Date
  }): IResubmitComplianceContract => {
    return {
      retailerCRSA: hashKey,
      detailType:
        type === 'event'
          ? RESUBMIT_OCS_KEYS.EVENT // Inventory event reports(hourly reports)
          : RESUBMIT_OCS_KEYS.POSITION, // Inventory position reports(daily reports)
      startDate: dayjs(startDate).utc().toISOString(),
      endDate: dayjs(endDate).utc().toISOString(),
    }
  }

  const handleSubmit = async () => {
    if (!selectedCRSA || !startDate || !endDate || disableSubmit) {
      return
    }

    try {
      setIsLoading(true)

      const eventPayload = createPayload({
        hashKey: selectedCRSA,
        type: 'event',
        startDate,
        endDate,
      })

      await resubmitCompliance({
        gateway: Endpoints.AUTHZ_GATEWAY_URL,
        payload: eventPayload,
      })

      const positionPayload = createPayload({
        hashKey: selectedCRSA,
        type: 'position',
        startDate,
        endDate,
      })

      await resubmitCompliance({
        gateway: Endpoints.AUTHZ_GATEWAY_URL,
        payload: positionPayload,
      })

      message.success('Successfully re-submitted to OCS')
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while re-submitting to OCS')
    } finally {
      setIsLoading(false)

      reset()
    }
  }

  return (
    <Modal
      title='OCS Resubmission'
      visible={true}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
    >
      <p className='mb-1'>1. Select a CRSA number</p>
      <Select
        value={selectedCRSA}
        onChange={(value) => setSelectedCRSA(value)}
        placeholder='Select a CRSA number'
        allowClear
        dropdownMatchSelectWidth={true}
        style={{ width: '100%' }}
      >
        {[...crsaHashKeyMap.keys()].map((key) => (
          <Select.Option key={key} value={key}>
            {key.replace('CRSA', 'CRSA ')}
          </Select.Option>
        ))}
      </Select>

      <div className='mt-4'>
        <p className='mb-1'>
          2. Select date range{' '}
          <Typography.Text type='danger'>(Maximum range is a month)</Typography.Text>
        </p>

        <DatePicker.RangePicker
          className='d-flex'
          disabled={!selectedCRSA}
          showTime={false}
          onChange={([selectedStartDate, selectedEndDate]) => {
            setStartDate(selectedStartDate ? selectedStartDate.startOf('day').toDate() : null)
            setEndDate(selectedEndDate ? selectedEndDate.endOf('day').toDate() : null)
          }}
          disabledDate={(currentDate) => {
            return currentDate ? currentDate > dayjs().endOf('day') : false
          }}
        />

        {invalidDateRange && (
          <Typography.Text type='danger' className='mt-2 d-block'>
            Please select a date range of a month or less.
          </Typography.Text>
        )}

        <Button type='primary' className='mt-4' disabled={disableSubmit} onClick={handleSubmit}>
          Re-submit
        </Button>
      </div>

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 99999,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
          className='d-flex flex-column justify-content-center align-items-center'
        >
          <Spin size='large' className='mb-4' />

          <p>Re-submitting to OCS...</p>

          <p>Please don't close this modal.</p>
        </div>
      )}
    </Modal>
  )
}
